import {
  ApplicationConfig,
  DOCUMENT,
  importProvidersFrom,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {
  provideRouter,
  RouteReuseStrategy,
  Router,
  withDisabledInitialNavigation,
  withNavigationErrorHandler,
} from '@angular/router';
import {routes} from './app.routes';
import {LpAuthRouteReuseStrategy} from '@/app/core/routing/lp-auth-route-reuse.strategy';
import {
  clearChunkReloadFlag,
  reloadOnChunkLoadError,
} from '@/app/core/utils/chunk-load-error.util';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {authApiInterceptor} from '@/app/core/interceptors/auth-api.interceptor';
import {loadingInterceptor} from '@/app/core/interceptors/loading.interceptor';
import {apiErrorInterceptor} from '@/app/core/interceptors/api-error.interceptor';

import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {providePrimeNG} from 'primeng/config';
import {ToastModule} from 'primeng/toast';
import {MuwaridPreset} from '@/app/styles/muwarid-preset';
import {ConfirmationService, MessageService} from 'primeng/api';
import {provideTranslateService, TranslateService} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {provideStore} from '@ngrx/store';
import {reducers} from '@/stores';
import {layoutStore} from '@/app/layout/store/layout.store';
import {firstValueFrom} from 'rxjs';
import {provideEchartsCore} from 'ngx-echarts';
import {unregisterAngularServiceWorker} from '@/app/core/utils/unregister-ngsw.util';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(
      routes,
      withDisabledInitialNavigation(),
      withNavigationErrorHandler((error) => {
        reloadOnChunkLoadError(error.error ?? error);
        throw error;
      }),
    ),
    {provide: RouteReuseStrategy, useClass: LpAuthRouteReuseStrategy},
    provideEchartsCore({
      echarts: () => import('echarts'),
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        authApiInterceptor,
        apiErrorInterceptor,
      ]),
    ),
    provideStore(reducers),
    // Reuses the server-rendered /lp/** DOM and its GET responses (transfer cache)
    // instead of re-rendering from scratch; client-only routes are unaffected.
    provideClientHydration(withEventReplay()),
    provideAnimations(),
    importProvidersFrom(ToastModule),
    providePrimeNG({
      theme: {
        preset: MuwaridPreset,
        options: {darkModeSelector: '.dark'},
      },
    }),
    MessageService,
    ConfirmationService,
    // No fallbackLang here: v18 would load it eagerly through HttpClient, whose
    // apiErrorInterceptor injects TranslateService (NG0200). The initializer sets it.
    provideTranslateService(),
    ...provideTranslateHttpLoader({prefix: '/i18n/', suffix: '.json'}),
    provideAppInitializer(() => {
      clearChunkReloadFlag();
      const translate = inject(TranslateService);
      // The server reads bundled translations through its TranslateLoader (app.config.server.ts).
      if (!isPlatformBrowser(inject(PLATFORM_ID))) {
        translate.setFallbackLang('en');
        return firstValueFrom(translate.use(layoutStore.language()));
      }
      return Promise.all([
        fetch('/i18n/en.json').then((r) => r.json()),
        fetch('/i18n/ar.json').then((r) => r.json()),
      ]).then(([en, ar]) => {
        translate.setTranslation('en', en);
        translate.setTranslation('ar', ar);
        translate.setFallbackLang('en');
        const lang = layoutStore.language();
        return firstValueFrom(translate.use(lang));
      });
    }),

    provideAppInitializer(() => {
      const router = inject(Router);
      const doc = inject(DOCUMENT);
      const path = doc.location?.pathname ?? '/';
      // The server only renders /lp/** (app.routes.server.ts); other pages aren't server-safe.
      if (!isPlatformBrowser(inject(PLATFORM_ID))) {
        return path.startsWith('/lp/') ? router.initialNavigation() : undefined;
      }
      // Set by server.ts from the reverse proxy's X-Tenant-Name on a tenant's own domain.
      const tenant = doc
        .querySelector('meta[name="tenant-name"]')
        ?.getAttribute('content');
      if (tenant && (path === '/' || path === '')) {
        return router.navigateByUrl(`/lp/${tenant}`, {
          skipLocationChange: true,
        });
      }
      return router.initialNavigation();
    }),
    provideAppInitializer(() => unregisterAngularServiceWorker()),
  ],
};
